import { useEffect, useState } from 'react'
import { Search, Filter } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { Course } from '../types/database'
import CourseCard from '../components/courses/CourseCard'

interface CoursesProps {
  user: any
}

export default function Courses({ user }: CoursesProps) {
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTheme, setSelectedTheme] = useState<string>('all')
  const [selectedLevel, setSelectedLevel] = useState<string>('all')

  useEffect(() => {
    fetchCourses()
  }, [selectedTheme, selectedLevel])

  const fetchCourses = async () => {
    setLoading(true)
    let query = supabase.from('courses').select('*').eq('is_published', true)

    if (selectedTheme !== 'all') {
      query = query.eq('theme', selectedTheme)
    }

    if (selectedLevel !== 'all') {
      query = query.eq('level', selectedLevel)
    }

    const { data } = await query.order('rating', { ascending: false })

    setCourses(data || [])
    setLoading(false)
  }

  const filteredCourses = courses.filter((course) =>
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-section text-neutral-800 mb-2">
            Explorar Cursos
          </h1>
          <p className="text-neutral-600">
            Aprende sobre temas que importan y aplica tu conocimiento en tu comunidad
          </p>
        </div>

        {/* Barra de búsqueda y filtros */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Búsqueda */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar cursos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-education"
                />
              </div>
            </div>

            {/* Filtros */}
            <div className="flex gap-4">
              <select
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                className="px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-education"
              >
                <option value="all">Todos los temas</option>
                <option value="Política">Política</option>
                <option value="Medio Ambiente">Medio Ambiente</option>
                <option value="Justicia Social">Justicia Social</option>
                <option value="Economía">Economía</option>
                <option value="Educación">Educación</option>
              </select>

              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-education"
              >
                <option value="all">Todos los niveles</option>
                <option value="principiante">Principiante</option>
                <option value="intermedio">Intermedio</option>
                <option value="avanzado">Avanzado</option>
              </select>
            </div>
          </div>
        </div>

        {/* Grid de cursos */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-md h-96 animate-pulse"></div>
            ))}
          </div>
        ) : filteredCourses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Filter className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-600">No se encontraron cursos con estos filtros</p>
          </div>
        )}
      </div>
    </div>
  )
}
